import { BackgroundTypes } from '@Map/panel/types';
import { EsriBasemapId } from '../types';

/**
 * Maps the platform-agnostic `BackgroundTypes` enum to ESRI-specific basemap style IDs.
 *
 * This mapping layer handles the translation between:
 * - UI layer: `BackgroundTypes` (e.g., `Streets`, `Satellite`, `OSMStandard`)
 * - ESRI API: `EsriBasemapId` (e.g., `'arcgis/navigation'`, `'open/streets'`)
 *
 * @param bgType - The background type from the UI layer
 * @returns The corresponding `EsriBasemapId` style ID
 */
export function mapBackgroundTypeToEsriBasemap(
	bgType: BackgroundTypes | EsriBasemapId,
): EsriBasemapId {
	// If it's already a EsriBasemapId value, return it directly
	if (Object.values(EsriBasemapId).includes(bgType as EsriBasemapId)) {
		return bgType as EsriBasemapId;
	}

	// Map BackgroundTypes to EsriBasemapId style IDs
	switch (bgType) {
		case BackgroundTypes.Streets:
			return EsriBasemapId.Navigation;
		case BackgroundTypes.Satellite:
			return EsriBasemapId.Imagery;
		case BackgroundTypes.Dark:
			return EsriBasemapId.DarkGray;
		case BackgroundTypes.Outdoors:
			return EsriBasemapId.Topographic;
		case BackgroundTypes.OSMStandard:
			return EsriBasemapId.OpenStreets;
		case BackgroundTypes.OSMRelief:
			return EsriBasemapId.OpenStreetsRelief;
		case BackgroundTypes.OSMStreets:
			return EsriBasemapId.OpenStreets;
		case BackgroundTypes.OSMStreetsRelief:
			return EsriBasemapId.OpenStreetsRelief;
		case BackgroundTypes.OSMLightGray:
			return EsriBasemapId.LightGray;
		case BackgroundTypes.OSMDarkGray:
			return EsriBasemapId.OpenDarkGray;
		case BackgroundTypes.ArcGISImagery:
			return EsriBasemapId.Imagery;
		case BackgroundTypes.ArcGISImageryLabels:
			return EsriBasemapId.LabelsOnly_Imagery;
		default:
			console.warn(
				`Unknown background type: ${bgType}, defaulting to OpenStreets`,
			);
			return EsriBasemapId.OpenStreets;
	}
}

/**
 * Maps a `EsriBasemapId` style ID back to the platform-agnostic `BackgroundTypes` enum.
 *
 * This reverse mapping is useful for:
 * - Synchronizing UI state when basemap changes programmatically
 * - Updating layer panel selection indicators
 * - Event callbacks that need to report the current background type
 *
 * @param basemapId - The `EsriBasemapId` style ID
 * @returns The corresponding `BackgroundTypes` value
 */
export function mapEsriBasemapToBackgroundType(
	basemapId: EsriBasemapId | string,
): BackgroundTypes {
	switch (basemapId) {
		case EsriBasemapId.OpenStreets:
			return BackgroundTypes.OSMStandard;
		case EsriBasemapId.OpenStreetsRelief:
			return BackgroundTypes.OSMStreetsRelief;
		case EsriBasemapId.OpenDarkGray:
			return BackgroundTypes.OSMDarkGray;
		case EsriBasemapId.Imagery:
		case EsriBasemapId.ImageryWithoutLabels:
			return BackgroundTypes.ArcGISImagery;
		case EsriBasemapId.LabelsOnly_Imagery:
			return BackgroundTypes.ArcGISImageryLabels;
		case EsriBasemapId.Streets:
		case EsriBasemapId.Navigation:
			return BackgroundTypes.Streets;
		case EsriBasemapId.NavigationNight:
		case EsriBasemapId.StreetsNight:
		case EsriBasemapId.DarkGray:
			return BackgroundTypes.Dark;
		case EsriBasemapId.StreetsWithRelief:
			return BackgroundTypes.OSMStreetsRelief;
		case EsriBasemapId.Terrain:
		case EsriBasemapId.Topographic:
		case EsriBasemapId.Oceans:
		case EsriBasemapId.ChartedTerritory:
			return BackgroundTypes.Outdoors;
		case EsriBasemapId.LightGray:
			return BackgroundTypes.OSMLightGray;
		case EsriBasemapId.OpenHybrid:
			return BackgroundTypes.Satellite;
		default:
			console.warn(
				`Unknown ESRI basemap ID: ${basemapId}, defaulting to Streets`,
			);
			return BackgroundTypes.Streets;
	}
}
